import { useState, useEffect, useCallback } from 'react'
import { useMutation } from "@tanstack/react-query"

import { showLoading } from 'services/util'

import { updateAuctionMutation } from '../graphql/Mutations'
import { graphql  } from '../api'

import css from './sass/createedit.module.scss'
import themeCss from '../components/sass/theme.module.scss'
import { handleEscapeKey } from '../components/utils'

export default function CategoriesModal({ auction, refreshAdminAuctionQuery, setHideCloseModalButton, setShowModal }) {
  const auctionId = auction.AuctionId

  if (!setHideCloseModalButton) setHideCloseModalButton = () => {}

  const [categories, setCategories] = useState(auction?.ItemCategories || [])
  const [isEditing, setIsEditing] = useState(true)
  const [draftCategories, setDraftCategories] = useState(auction?.ItemCategories || [])

  const [invalidCategories, setInvalidCategories] = useState(true)
  const [activeCategories, setActiveCategories] = useState([])

  console.log('auction', auction)

    const { data: updateAuctionResponse, error, isPending, mutate: saveAuction } = useMutation({
      mutationFn: (input) => {
        return graphql({ query: updateAuctionMutation, variables: { input: input } })
      },
    })

  const toggleModal = useCallback(() => {
    setShowModal(false)
  }, [setShowModal])

  // use effect to see which categories are used in the items in the auction
  useEffect(() => {
    const auctionItems = auction?.Items || []
    const fixedItems = auction?.FixedItems || []

    if (auctionItems.length === 0 && fixedItems.length === 0) {
      setActiveCategories([])
      return
    }

    const uniqueCategories = new Set()

    auctionItems.forEach(item => {
      if (item.Category != null) uniqueCategories.add(item.Category)
    })

    fixedItems.forEach(item => {
      if (item.Category != null) uniqueCategories.add(item.Category)
    })

    setActiveCategories([...uniqueCategories])
    
  }, [auction])

  useEffect(() => {
    const onKeyDown = (event) => handleEscapeKey(event, toggleModal)
    //add event listener for when modal is mounted
    document.addEventListener('keydown', onKeyDown)
    //clean up when modal is unmounted
    return () => document.removeEventListener('keydown', onKeyDown)

  }, [toggleModal])

  useEffect(() => {
      showLoading(isPending)
  
      if (error) {
        console.log("Auction not created: ", error)
        console.log(error.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
      }
      else if (updateAuctionResponse) {
        console.log(updateAuctionResponse)
        console.log('Auction updated: ', updateAuctionResponse.data.updateAuction.AuctionId)

        setCategories(draftCategories)
        setIsEditing(false)
        setShowModal(false)
  
        refreshAdminAuctionQuery()
        //trim after submission to update input fields
      }
    }, [updateAuctionResponse, error, isPending, refreshAdminAuctionQuery])

  useEffect(() => {
    showLoading(isPending, "EditCategories")
  }, [isPending])

  // Keep local state in sync if the prop changes from the parent/database
  useEffect(() => {
    if (!isEditing) {
      setDraftCategories(categories)
    }
  }, [categories, isEditing])

  const isCategoryInUse = (category) => {
    return activeCategories.includes(category)
  }

  useEffect(() => {

    function areDraftsValid(activeCategories, draftCategories) {
      // Check for empty strings
      if (draftCategories?.some(item => item === "")) return false

      // Check for duplicates
      const unique = new Set(draftCategories)
      if (unique.size !== draftCategories?.length) return false

      //check to make sure all active categories are in the drafts
      const draftSet = new Set(draftCategories)
      return activeCategories.every(category => draftSet.has(category))
    }

    // if there are draft categories, check to see if they are empty or duplicates
    if (draftCategories.length > 0) {
      setInvalidCategories(!areDraftsValid(activeCategories, draftCategories))
    } else {
      if (activeCategories.length === 0) setInvalidCategories(false)
      else setInvalidCategories(true)
    }


  }, [draftCategories])

  const onSubmit = async () => {
    const input = {
      AuctionId: auctionId,
      ItemCategories: draftCategories
    }

    saveAuction(input)
  }

  const handleEdit = () => {
    setDraftCategories(categories)
    setIsEditing(true)
  }

  const handleDiscard = () => {
    setDraftCategories(categories)
    setIsEditing(false)
  }

  const handleChange = (index, value) => {
    const updated = [...draftCategories]
    updated[index] = value
    setDraftCategories(updated)
  }

   const handleAddCategory = () => {
    setDraftCategories([...draftCategories, ""])
  }

  const handleRemove = (index) => {
    const updated = draftCategories.filter((_, i) => i !== index)
    setDraftCategories(updated)
  }

  const moveCategory = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= draftCategories.length) return

    setDraftCategories((prev) => {
      const next = [...prev]

      const temp = next[fromIndex]
      next[fromIndex] = next[toIndex]
      next[toIndex] = temp

      return next
    })
  }

  const handleMoveUp = (index) => {
    moveCategory(index, index - 1)
  }

  const handleMoveDown = (index) => {
    moveCategory(index, index + 1)
  }

  return (
    <div className={css["categories-editor"]}>
      <div className={css["categories-header"]}>
        <h3>Item Categories</h3>
        <p>Item categories are entirely <b><u>optional</u></b>.</p>
        <p>Categories can help your attendees to filter on the Auction page itself.</p>
        <p>New categories cannot be empty, or duplicates of another category.</p>
        <p>You cannot delete or rename a category that currently has items attached to it. If you want to remove a category, please change the category on the associated items first.</p>
      </div>
      <div className={css["categories-list"]}>
        {draftCategories.map((category, index) => (
          <div className={css["categories-row"]} key={`${index}-input`}>
            {isEditing ? (
              <>
                <input type="text" className={css["categories-input"]} id={`category-input-${index}`}
                  value={category}
                  onChange={(e) => handleChange(index, e.target.value)}
                />
                
                <div className={css["categories-row-actions"]}>
                  <button className={css["categories-arrow-button"]}
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    > ⬆ </button>
                  <button className={css["categories-arrow-button"]}
                    onClick={() => handleMoveDown(index)}
                    disabled={index === draftCategories.length - 1}
                    > ⬇ </button>
                  <button className={css["categories-remove-button"]}
                    onClick={() => handleRemove(index)}
                    disabled={isCategoryInUse(category)}
                    > × </button>

                </div>
                {isCategoryInUse(category) &&<p>Category in use.</p>}
              </>
            ) : (
              <div className={css["categories-display"]}>{category}</div>
            )}
          </div>
        ))}
        {draftCategories.length === 0 && <p><b>No categories saved, add some below if you'd like to use this optional feature.</b></p>}
      </div>

      <div className={css["categories-button-row"]}>
        {!isEditing ? (
          <button className={`${themeCss['button']} ${themeCss['FilledBlue']} `} 
            onClick={handleEdit}>Edit Categories</button>
        ) : (<>
            <button className={`${themeCss['button']} ${themeCss['StrokeRed']} `} 
              onClick={handleDiscard}>
                Discard Changes
            </button>
            <button className={`${themeCss['button']} ${themeCss['FilledWhite']} `} 
              onClick={handleAddCategory}>
                Add Category</button>
            <button className={`${themeCss['button']} ${themeCss['FilledBlue']} `}
              disabled={invalidCategories}
              onClick={() => onSubmit()}>
                Save Changes</button>
          </>)}
      </div>
    </div>
  )
}
